import React from 'react';
import { AlertTriangle, Trash2, Info, CheckCircle, Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../ui/alert-dialog';

type DialogVariant = 'info' | 'warning' | 'danger' | 'success';

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: DialogVariant;
  loading?: boolean;
  confirmDisabled?: boolean;
  confirmButtonTestId?: string;
  requireConfirmText?: string;
}

const getVariantConfig = (variant: DialogVariant) => {
  switch (variant) {
    case 'danger':
      return {
        icon: <Trash2 className="h-5 w-5" />,
        iconClass: 'text-[#DC2626] bg-[#FEE2E2]',
        iconContainerClass: 'w-10 h-10 rounded-full',
        confirmClass: 'bg-[#DC2626] hover:bg-red-700 text-white',
      };
    case 'warning':
      return {
        icon: <AlertTriangle className="h-5 w-5" />,
        iconClass: 'text-yellow-600 bg-yellow-50',
        iconContainerClass: 'w-10 h-10 rounded-full',
        confirmClass: 'bg-yellow-500 hover:bg-yellow-600 text-white',
      };
    case 'success':
      return {
        icon: <CheckCircle className="h-5 w-5" />,
        iconClass: 'text-green-600 bg-green-50',
        iconContainerClass: 'w-10 h-10 rounded-full',
        confirmClass: 'bg-green-600 hover:bg-green-700 text-white',
      };
    case 'info':
    default:
      return {
        icon: <Info className="h-5 w-5" />,
        iconClass: 'text-primary bg-primary/10',
        iconContainerClass: 'w-10 h-10 rounded-full',
        confirmClass: 'bg-primary hover:bg-primary-dark text-white',
      };
  }
};

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = '確認',
  cancelText = 'キャンセル',
  variant = 'info',
  loading = false,
  confirmDisabled = false,
  confirmButtonTestId,
  requireConfirmText,
}) => {
  const [confirmInput, setConfirmInput] = React.useState('');
  const config = getVariantConfig(variant);

  return (
    <AlertDialog open={open} onOpenChange={(o) => !loading && !o && onClose()}>
      <AlertDialogContent>
        <div data-testid="confirm-dialog">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className={`flex items-center justify-center ${config.iconContainerClass} ${config.iconClass}`}>
              {config.icon}
            </div>
            <AlertDialogTitle>{title}</AlertDialogTitle>
          </div>
        </AlertDialogHeader>

        <div className="px-6 pb-2">
          {typeof message === 'string' ? (
            <AlertDialogDescription>{message}</AlertDialogDescription>
          ) : (
            message
          )}
        </div>

        {requireConfirmText && (
          <div className="px-6 pb-4">
            <label className="block text-sm font-medium mb-2">確認のため「{requireConfirmText}」と入力してください</label>
            <input
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm outline-none"
              data-testid="confirm-dialog-text-input"
            />
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>{cancelText}</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={loading || confirmDisabled || (requireConfirmText ? confirmInput !== requireConfirmText : false)}
            data-testid={confirmButtonTestId || 'confirm-delete-button'}
            className={config.confirmClass}
          >
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {loading ? '処理中...' : confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
};
